import { useEffect, useRef } from 'react';

import { createLogLocal } from './utils/createLogLocal';
import { detectSegment } from './utils/detectSegment';

import type { CurrentParsingState } from './@types/CurrentParsingState';

const log = createLogLocal('CardReader');

/**
 * Extracted fields:
 *    |card number|allNames|YY|MM|
 * Full card format:
 *    %B|card number|lname| fname|  |mname| |YY|MM| |SC|Discretionary Data|?
 * Certain matchable fields are ignored.
 */
const ISO7813Format =
  /([0-9]{1,19})\^([a-zA-Z /]+)[^^]*\^([0-9]{2})([0-9]{2}).*/;

/**
 * Default local state for parsing card reader input.
 */
const defaultCardReaderData: CurrentParsingState = {
  str: '',
  inSequence: false,
  segment: '',
  timeout: null,
  scrollLocked: false,
};

/**
 * Prevent page scrolling by locking the scroll position.
 * Avoids unwanted scrolling during name input detection from card reader.
 */
const preventScroll = () => {
  log('[preventScroll] Locking scroll position.');
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

  window.onscroll = () => {
    window.scrollTo(scrollLeft, scrollTop);
  };
};

/**
 * Allow page scrolling: unlocks the scroll position.
 */
const allowScroll = () => {
  setTimeout(() => {
    log('[allowScroll] Unlocking scroll position (delayed).');
    window.onscroll = null;
  }, 700); // Delay to ensure any final scroll events are handled
};

/**
 * Parses card data from a magnetic stripe string following ISO 7813 format.
 * Extracts card number, name fields, expiration date, and other metadata.
 *
 * @param rawData - The raw magnetic stripe data string to parse
 * @returns Parsed card data object if valid, null if data doesn't match expected format
 */
const parseCardData = (rawData: string) => {
  const cleanedData = rawData.replaceAll('Shift', '');

  log('[parseCardData] Attempting to parse card data:', {
    rawData: cleanedData,
    parsedData: cleanedData.match(ISO7813Format),
  });

  const match = cleanedData.match(ISO7813Format);

  // If the data doesn't match ISO 7813 format, return null
  if (!match) {
    log('[parseCardData] Data does not match ISO 7813 format.');
    return null;
  }

  const [
    ,
    cardNumber = '',
    allNames = '',
    YY = '',
    MM = '',
    serviceCode = '',
    discretionaryData = '',
  ] = match;

  // Getting the names isn't as easy as it should be. For different cards,
  // the last/first name are either delimited by / chars (the ISO standard)
  // or spaces. Sometimes there's a middle initial at the middle or last.
  let lastName = '';
  let firstName = '';

  if (allNames.includes('/')) {
    // ISO format: LASTNAME/FIRSTNAME
    const [lastNameRaw = '', firstNameRaw = ''] = allNames.split('/');
    lastName = lastNameRaw.trim();
    firstName = firstNameRaw.trim();
  } else {
    // Space-delimited format: split on spaces and remove empty items
    const allNamesList = allNames.split(' ').filter(name => name.length >= 1);
    // First item is first name, last item is last name
    firstName = allNamesList[0] || '';
    lastName = allNamesList[allNamesList.length - 1] || '';
  }

  return {
    cardNumber,
    lastName,
    firstName,
    YY,
    MM,
    serviceCode,
    discretionaryData,
  };
};

/**
 * Parsed card data returned to the onComplete callback
 */
export type CardData = {
  cardNumber: string;
  lastName: string;
  firstName: string;
  YY: string;
  MM: string;
  serviceCode: string;
  discretionaryData: string;
};

/**
 * Props for the CardReader component
 */
export type CardReaderProps = {
  onComplete: (cardData: CardData) => void;
};

/**
 * To test, paste in this string on a page with this component:
 *  %B4242424242424242^BLAIR/COLBY               ^23012010000000099000000?;44242424242424242=23012010000009900000?
 */
export const CardReader = ({ onComplete }: CardReaderProps) => {
  // Use a ref to always have access to the latest onComplete callback.
  // This prevents stale closure issues where event listeners capture
  // an outdated callback from the initial render.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const localState = { ...defaultCardReaderData } as CurrentParsingState;

  const resetLocalState = () => {
    log('[CardReader] Resetting local state.');
    Object.assign(localState, defaultCardReaderData);
    allowScroll();
  };

  const handleKeyEvent = ({ key }: { key: string }) => {
    if (!localState.inSequence) {
      if (key === '%') {
        localState.inSequence = true;
        localState.str = '%';
        localState.segment = 'startSymbol';
      }
    }

    // Handle when we're already in a sequence.
    else if (localState.segment !== 'end' && localState.segment) {
      const segment = detectSegment(localState.str, localState.segment, key);
      localState.segment = segment;

      // If something throws us out of the segment, reset state. This tells us
      // it was never really a card swipe, it just looked like one initially.
      if (!segment) resetLocalState();
      // Accumulate data if we're still in a segment
      else if (segment !== 'end') {
        if (segment === 'cardNum' && !localState.scrollLocked) {
          localState.scrollLocked = true;
          preventScroll();
        }
        localState.str += key;
      }

      // Handle the end of the segment
      else if (key === 'Enter' || segment === 'end') {
        const rawData = localState.str;
        localState.str = '';

        const parsedData = parseCardData(rawData);

        resetLocalState();

        // Only call onComplete if we successfully parsed valid card data
        if (parsedData) {
          onCompleteRef.current(parsedData);
        } else {
          log('[handleKeyEvent] Failed to parse card data from sequence.');
        }
      }
    } else {
      log(`handleKeyEvent :: key: ${key}, localState: ${localState}`);
    }
  };

  const handlePasteEvent = (ev: ClipboardEvent) => {
    log('[handlePasteEvent] Paste event detected.');

    // Stop data actually being pasted in.
    ev.stopPropagation();
    ev.preventDefault();

    // Get pasted data via clipboard API.
    const clipboardData = ev.clipboardData || (window as any).clipboardData;
    const pastedData = clipboardData.getData('Text');
    const parsedData = parseCardData(pastedData);

    // Only call onComplete if we successfully parsed valid card data
    if (parsedData) {
      onCompleteRef.current(parsedData);
    } else {
      log('[handlePasteEvent] Failed to parse card data from paste.');
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyEvent);
  }, []);
  useEffect(() => {
    document.addEventListener('paste', handlePasteEvent);
  }, []);
  // componentWillUnmount: Remove all event listeners to avoid buildup of callbacks
  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', handleKeyEvent);
      document.removeEventListener('paste', handlePasteEvent);
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return null;
};
